import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";

export type CorrectionType = "in_only" | "out_only" | "both";

export type RegularisationStatus = "pending" | "approved" | "rejected" | "cancelled";

export type RegularisationReasonType =
  | "whatsapp_location"
  | "forgot_mark_in"
  | "forgot_mark_out"
  | "forgot_attendance"
  | "system_issue"
  | "client_visit"
  | "official_travel"
  | "others";

export interface RegularisationRow {
  id: string;
  userId: string;
  userName: string | null;
  userEmail: string | null;
  attendanceId: string | null;
  attendanceDate: string | null;
  correctionType: CorrectionType;
  correctedInTime: string | null;
  correctedOutTime: string | null;
  inLocationLabel: string;
  outLocationLabel: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId: string | null;
  status: RegularisationStatus;
  submittedAt: string | null;
  submittedById: string | null;
  submittedByName: string | null;
  isProxy: boolean;
  bypassReason: string;
  reviewedAt: string | null;
  reviewedById: string | null;
  reviewedByName: string | null;
  reviewNote: string;
  cancelReason: string;
  createdAt: string | null;
}

export interface ProxyTarget {
  id: string;
  name: string;
  email: string;
  role: string;
}

export interface RegularisationListResponse {
  results: RegularisationRow[];
  nextCursor: string | null;
  prevCursor: string | null;
}

export interface RegularisationCreateInput {
  attendanceDate: string;
  correctionType: CorrectionType;
  correctedInTime?: string | null;
  correctedOutTime?: string | null;
  inLocationLabel?: string;
  outLocationLabel?: string;
  reasonType: RegularisationReasonType;
  reasonDetails: string;
  attachmentId?: string | null;
  // Proxy filing (Technical Admin only). Setting ``targetUserId`` routes the
  // request to ``submit_proxy_regularisation`` server-side, bypassing the
  // 3-day window.
  targetUserId?: string | null;
  bypassReason?: string;
}

const listKey = (params: object) => ["regularisations", "list", params] as const;

export function useRegularisations(
  params: {
    scope?: "self" | "all" | "submitted_by_self";
    status?: RegularisationStatus | "";
    userId?: string;
    from?: string;
    to?: string;
  } = {},
  enabled: boolean = true,
) {
  return useQuery<RegularisationListResponse>({
    queryKey: listKey(params),
    queryFn: () =>
      api
        .get<RegularisationListResponse>("/attendance-regularisations", {
          params: {
            scope: params.scope || "self",
            status: params.status || undefined,
            userId: params.userId || undefined,
            from: params.from || undefined,
            to: params.to || undefined,
          },
        })
        .then((r) => r.data),
    enabled,
  });
}

export function useSubmitRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RegularisationCreateInput) =>
      api
        .post<RegularisationRow>("/attendance-regularisations", input)
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

export function useProxyTargets(search: string, enabled: boolean = true) {
  return useQuery<{ results: ProxyTarget[] }>({
    queryKey: ["regularisations", "proxy-targets", search],
    queryFn: () =>
      api
        .get<{ results: ProxyTarget[] }>(
          "/attendance-regularisations/proxy-targets",
          { params: { search: search || undefined } },
        )
        .then((r) => r.data),
    enabled,
  });
}

export function useCancelRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/cancel`, {
          reason: reason ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["regularisations"] }),
  });
}

export function useReviewRegularisation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      decision,
      note,
    }: {
      id: string;
      decision: "approved" | "rejected";
      note?: string;
    }) =>
      api
        .post<RegularisationRow>(`/attendance-regularisations/${id}/review`, {
          decision,
          note: note ?? "",
        })
        .then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["regularisations"] });
      qc.invalidateQueries({ queryKey: ["attendance"] });
      qc.invalidateQueries({ queryKey: ["leaves"] });
    },
  });
}
